import { Eye, UserCheck, UserX } from "lucide-react";
import React from "react";

import { Button, StatusBadge } from "@/components/ui";
import type { SellerListItem } from "@/hooks/useSellers";
import { withAppBase } from "@/lib/appPaths";
import { fmtVal } from "@/lib/orders";
import { cn, titleCase } from "@/lib/utils";

import { CopyBtn } from "./CopyBtn";
import {
  planColors,
  syncStatusBadge,
  workspaceStatusBadge,
} from "./seller.constants";

function avatarInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

interface SellersTableRowProps {
  s: SellerListItem;
  pending: boolean;
  onToggle: (seller: SellerListItem) => void;
}

export const SellersTableRow: React.FC<SellersTableRowProps> = ({
  s,
  pending,
  onToggle,
}) => (
  <tr
    data-testid={`seller-row-${s.id}`}
    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
    onClick={() => {
      window.location.href = withAppBase(`/sellers/${s.id}`);
    }}
  >
    <td className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-xs font-bold text-indigo-700 dark:text-indigo-300 shrink-0">
          {avatarInitials(s.name)}
        </div>
        <div>
          <p className="font-medium text-slate-900 dark:text-slate-100">
            {s.name}
          </p>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {s.ownerEmail ?? "—"}
          </p>
        </div>
      </div>
    </td>

    <td className="px-4 py-3">
      <div className="flex items-center gap-1.5">
        <span
          title={s.id}
          className="font-mono text-xs text-slate-500 dark:text-slate-400"
        >
          {s.id.slice(0, 12)}…
        </span>
        <CopyBtn text={s.id} />
      </div>
    </td>

    <td className="px-4 py-3">
      <span
        className={cn(
          "px-2 py-0.5 text-[11px] font-semibold rounded-full border",
          planColors[s.plan] ?? planColors["FREE"],
        )}
      >
        {titleCase(s.plan)}
      </span>
    </td>

    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
      {s.totalProducts.toLocaleString()}
    </td>

    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
      {s.activeOrders.toLocaleString()}
    </td>

    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
      {fmtVal(s.revenue30d, "currency")}
    </td>

    <td className="px-4 py-3">
      {s.lowStockCount > 0 ? (
        <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
          {s.lowStockCount} low
        </span>
      ) : (
        <span className="text-xs text-slate-400">OK</span>
      )}
    </td>

    <td className="px-4 py-3">
      <StatusBadge
        status={syncStatusBadge[s.syncStatus] ?? s.syncStatus}
        showDot
      />
    </td>

    <td className="px-4 py-3">
      <StatusBadge
        status={workspaceStatusBadge[s.status] ?? s.status}
        showDot
      />
    </td>

    <td className="px-4 py-3">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          data-testid={`view-seller-${s.id}`}
          onClick={(e) => {
            e.stopPropagation();
            window.location.href = withAppBase(`/sellers/${s.id}`);
          }}
        >
          <Eye className="w-4 h-4" />
        </Button>
        {/* CLOSED is an end state, not a block: reopening it is not this action. */}
        <Button
          variant="ghost"
          size="sm"
          title={
            s.status === "CLOSED"
              ? "Closed accounts cannot be reopened here"
              : s.status === "ACTIVE"
                ? "Block this seller"
                : "Unblock this seller"
          }
          disabled={pending || s.status === "CLOSED"}
          data-testid={`toggle-seller-${s.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(s);
          }}
        >
          {s.status === "ACTIVE" ? (
            <UserX className="w-4 h-4 text-rose-600 dark:text-rose-400" />
          ) : (
            <UserCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          )}
        </Button>
      </div>
    </td>
  </tr>
);
